import { useSelector } from "react-redux";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAppDispatch } from "../redux";
import { useEffect, useState } from "react";
import { getSurveyAnswers } from "../redux/reducer/surveyHistoryReducer";
import { getCitizenById } from "../redux/reducer/citizensReducer";
import { getSurveyFormField } from "../redux/reducer/surveyReducer";
import {
  getFormattedDateTime,
  getTranslatedLabel,
  getUIFormattedDate,
} from "../utils/helpers";
import HeaderModal from "../components/headerModal";
import HeaderSection from "../components/header-section";
import TitleModal from "../components/common/titleModal";
import TextParagraph from "../components/common/textParagraph";
import moment from "moment";
import React from "react";

// Component starts
const CitizenSurveyDetail = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  /**
   * Get survey ids from url.
   */
  const { id } = useParams();
  const location = useLocation();
  const { urlHistory, citizenId, surveyFieldId } = location.state || {};

  /**
   * redux hooks.
   */
  const { citizens } = useSelector((state: any) => state);
  const { editCitizen } = citizens;

  /**
   * state variables.
   */
  const [surveyAnswers, setSurveyAnswers] = useState<any>(null);
  const [surveyFields, setSurveyFields] = useState<any>(null);

  /**
   * Create title variable for HeaderModal.
   */
  const citizenTitle = `${editCitizen?.crm?.extraFields?.fiscalcode?.value} - ${editCitizen?.name} ${editCitizen?.surname}`;

  useEffect(() => {
    if (id && citizenId && surveyFieldId) {
      fetchData(id, citizenId, surveyFieldId);
    } else {
      navigate("/page-not-found");
    }
  }, [id, citizenId, surveyFieldId]);

  /**
   * Get survey answers and citizen details.
   * @param id - survey Id
   * @param citizenId - citizen Id
   * @param fieldId - survey formField Id
   */
  const fetchData = async (id: string, citizenId: string, fieldId: string) => {
    dispatch(getCitizenById(citizenId));
    const answers = await dispatch(getSurveyAnswers(id, citizenId));
    const surveyFields = await dispatch(getSurveyFormField(fieldId));
    setSurveyAnswers(answers);
    setSurveyFields(surveyFields);
  };

  /**
   * Render survey fields and label.
   */
  const getSurveyLabelWithValue = (label: any, value: any) => {
    return (
      <div className="survey-field">
        <label className="fw-bold label-sm">{label}</label>
        <label className="label-sm">{value}</label>
      </div>
    );
  };
  /**
   * Render survey fields based on type.
   */
  const renderSurveyField = (formField: any) => {
    const fieldLabel = getTranslatedLabel(formField.translations);
    const fieldAnswer = surveyAnswers?.answers[formField.name];

    switch (formField.type) {
      case "title":
        return <TitleModal title={fieldLabel} />;
      case "paragraph":
        return <TextParagraph title={fieldLabel} />;
      case "day":
      case "date":
        return (
          <>
            {fieldAnswer &&
              getSurveyLabelWithValue(
                fieldLabel,
                getUIFormattedDate(fieldAnswer)
              )}
          </>
        );
      case "boolean":
        return (
          <>
            {fieldAnswer && (
              <div className="survey-field">
                <label
                  className="fw-bold label-sm"
                  dangerouslySetInnerHTML={{ __html: fieldLabel }}
                />
                <label className="label-sm">
                  {fieldAnswer ? t("yes") : t("no")}
                </label>
              </div>
            )}
          </>
        );
      default:
        return (
          <>{fieldAnswer && getSurveyLabelWithValue(fieldLabel, fieldAnswer)}</>
        );
    }
  };

  return (
    <>
      <HeaderModal
        title={citizenTitle}
        textButtonRight={t("new-group-link-modal.error-back")}
        handleClick={() => navigate(urlHistory)}
      />
      <div className="page-title">
        <HeaderSection
          visibleButton={false}
          titleSection={
            surveyFields ? getTranslatedLabel(surveyFields?.title) : ""
          }
        />
      </div>

      <div className="container-box mb-3 d-flex justify-content-center">
        <div className="content col-md-6">
          <div className="col-md-11 mt-4">
            {/* Render survey field with answers */}
            {surveyFields?.list?.map((formField: any, i: any) => {
              return (
                <React.Fragment key={i}>
                  {renderSurveyField(formField)}
                </React.Fragment>
              );
            })}

            {/* Display createdOn date */}
            <div className="survey-field mb-4">
              <label className="fw-bold label-sm">{t("createdOn")}</label>
              <label className="label-sm">
                {getFormattedDateTime(surveyAnswers?.createdOn)}
              </label>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default CitizenSurveyDetail;
